/**
 * TODO: Add i18n
 * @see https://react.i18next.com/
 */

/**
 * TODO: Link with subscriptions API to get user active subscriptions
 * @see https://www.figma.com/design/9FuhY7T8ebPFopIgG6pNMM/Viome-App-4.0-Handoff?node-id=4396-22371&node-type=instance&m=dev
 */

import {
  Sidebar,
  SidebarMenu,
  SidebarFooter,
  SidebarHeader,
  SidebarContent,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@design-system/Sidebar';
import { CheckIcon } from 'lucide-react';
import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import { HOME_PATH } from '@/constants/paths';
import { LOGO_WHITE } from '@/constants/images';
import { DASHBOARD_ROUTES } from './DashboardLayout.constant';
import { DashboardLayoutNavItem } from './DashboardLayoutNavItem';
import { useBootup } from '@hooks/useBootup';
import { getKitName } from '@utils/getKitName';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';

export type DashboardLayoutSidebarProps = React.ComponentPropsWithoutRef<
  typeof Sidebar
>;

const renderedSidebarRoutes = (isCancerDetectUser: boolean, cancerDetectClicked: boolean) => {
  const filteredRoutes =
    (isCancerDetectUser || cancerDetectClicked) ?
      DASHBOARD_ROUTES.filter((route) => !route.hideIconForCancerDetect)
      : DASHBOARD_ROUTES;
  return filteredRoutes.map(route => (
    <li key={route.label}>
      <SidebarMenuButton asChild>
        <DashboardLayoutNavItem
          to={route.to}
          icon={route.icon}
          label={route.label}
          activeIcon={route.activeIcon}
        />
      </SidebarMenuButton>
    </li>
  ));
};

const renderedSubscriptionPanelLinks = (
  kitType: string,
  preRnRKitType: string,
  isCancerDetectUser: boolean,
  isWellNessKitUser: boolean,
  onClickRoute: (labelType: string) => void,
  isCancerDetectClicked: boolean,
) => {

  const SUBSCRIPTIONS_ROUTES = [
    { label: getKitName(kitType) || getKitName(preRnRKitType), icon: '', to: '/', active: !isCancerDetectClicked, hideOption: !isWellNessKitUser },
    { label: 'CancerDetect', icon: '', to: '/cancer-detect', active: isCancerDetectClicked, hideOption: !isCancerDetectUser },
  ];

  const resultedRoutes = SUBSCRIPTIONS_ROUTES.filter((route) => !route.hideOption)

  return resultedRoutes.map((route) => (
    <li key={route.label}>
      <SidebarMenuButton asChild>
        <Link
          to={route.to}
          className="typography-body gap-2 justify-start"
          onClick={() => onClickRoute(route?.label)}
        >
          {route.label} {route.active && <CheckIcon size={24} />}
        </Link>
      </SidebarMenuButton>
    </li>
  ));
}

export const DashboardLayoutSidebar = (props: DashboardLayoutSidebarProps) => {
  const { bootupState } = useBootup();
  const { isCancerDetectClicked, setIsCancerDetectClicked } = useCancerDetectUser();
  const { kitDetailsData, CDkitDetailsData, kitType, preRnRKitType } = bootupState || {};
  const hideSideBarOptionsForCancerDetectUsers = Boolean(CDkitDetailsData && !kitDetailsData);
  const displayTestsSection = Boolean(CDkitDetailsData && kitDetailsData);
  const isNoKitDetailsAvailable = (obj) => !obj || Object.keys(obj).length === 0;

  const onClickRoute = (labelType: string) => {
    if (labelType === 'CancerDetect') {
      setIsCancerDetectClicked(true);
    } else {
      setIsCancerDetectClicked(false);
    }
  };

  // Check if both kit types values are null and not display the sidebar.
  if (isNoKitDetailsAvailable(kitDetailsData) && isNoKitDetailsAvailable(CDkitDetailsData)) {
    return;
  }

  return (
    <Sidebar variant="sidebar" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link to={HOME_PATH}>
                <Image width={180} height={24} src={LOGO_WHITE} alt="Viome" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <ul>
          {renderedSidebarRoutes(hideSideBarOptionsForCancerDetectUsers, isCancerDetectClicked)}
        </ul>
      </SidebarContent>

      {displayTestsSection && (
        <SidebarFooter className="text-primary-foreground gap-4 flex flex-col">
          <span className="mx-3 typography-title font-serif italic border-b border-stroke-static py-4">
            My Tests
          </span>

          <ul>
            {renderedSubscriptionPanelLinks(
              kitType,
              preRnRKitType,
              Boolean(CDkitDetailsData),
              Boolean(kitDetailsData),
              onClickRoute,
              isCancerDetectClicked,
            )}
          </ul>
        </SidebarFooter>
      )}
    </Sidebar>
  );
};
